"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { useGetCart } from "@/features/cart/api/hooks/use-get-cart";
import { useRescheduleCart } from "@/features/cart/api/hooks/use-reschedule-cart";
import { useCartStore } from "@/features/cart/components/cart-provider";
import { haveSaturdays, haveSundays } from "@/features/cart/utils/dates";
import {
  type DateMoves,
  findDateMoves,
  moveItemDates,
} from "@/features/cart/utils/reschedule-dates";
import type { DietCatalog } from "@/features/diets/types";
import { DietDateManager } from "@/lib/helpers/diet-dates";
import type { LocaleType } from "@/types/locale.type";

export type RescheduledDiet = {
  id: string;
  diet: DietCatalog;
  previousDates: string[];
  dates: string[];
  movedDays: number;
};

export const useRescheduleUnavailableDates = (diets?: DietCatalog[]) => {
  const locale = useLocale() as LocaleType;
  const queryClient = useQueryClient();
  const cartId = useCartStore((state) => state.cartId);
  const setCart = useCartStore((state) => state.setCart);
  const {
    data: cart,
    isFetchedAfterMount,
    isSuccess,
  } = useGetCart({
    id: cartId || "",
    options: {
      enabled: !!cartId,
      refetchOnMount: "always",
      retry: false,
    },
  });
  const { mutate, isPending, variables } = useRescheduleCart();
  const [dialog, setDialog] = useState<{
    open: boolean;
    items: RescheduledDiet[];
  }>({ open: false, items: [] });
  const handledRef = useRef<string | null>(null);

  useEffect(() => {
    if (!(cartId && cart && diets && isFetchedAfterMount && isSuccess)) {
      return;
    }
    if (isPending) {
      return;
    }

    const entries = cart.items.flatMap(({ configurationItem }) => {
      const diet = diets.find(({ id }) => id === configurationItem.dietId);
      return diet ? [{ configurationItem, diet }] : [];
    });

    const signature = `${cartId}:${entries
      .map(
        ({ configurationItem }) =>
          `${configurationItem.id}=${configurationItem.dates.join(",")}`,
      )
      .join("|")}`;
    if (handledRef.current === signature) {
      return;
    }
    handledRef.current = signature;

    const moves: DateMoves = {};
    const changes: RescheduledDiet[] = [];

    for (const { configurationItem, diet } of entries) {
      const manager = new DietDateManager({
        diet,
        locale,
        saturdays: haveSaturdays(configurationItem.dates),
        sundays: haveSundays(configurationItem.dates),
      });
      const itemMoves = findDateMoves(configurationItem.dates, manager);
      if (!itemMoves?.length) {
        continue;
      }

      moves[configurationItem.id] = itemMoves;
      changes.push({
        id: configurationItem.id,
        diet,
        previousDates: configurationItem.dates,
        dates: moveItemDates(configurationItem, itemMoves).dates,
        movedDays: itemMoves.length,
      });
    }

    if (changes.length === 0) {
      return;
    }

    mutate(
      { cartId, moves },
      {
        onSuccess: ({ cart: nextCart }) => {
          queryClient.setQueryData(["cart", cartId], nextCart);
          setCart(nextCart);
          setDialog((current) => ({
            open: true,
            items: current.open ? [...current.items, ...changes] : changes,
          }));
        },
      },
    );
  }, [
    cart,
    cartId,
    diets,
    isFetchedAfterMount,
    isPending,
    isSuccess,
    locale,
    mutate,
    queryClient,
    setCart,
  ]);

  return {
    reschedulingIds: isPending && variables ? Object.keys(variables.moves) : [],
    rescheduledDiets: dialog.items,
    isDialogOpen: dialog.open,
    closeDialog: () => setDialog((current) => ({ ...current, open: false })),
  };
};
